import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { getPodColor, getPodSlot, POD_COLORS, POD_COLOR_NAMES } from "./podColors";
import { formatTime, localDateKey, getInitials, getEventKind, relDay, shortDay, normEmail } from "./dateUtils";
import useVisibleNotes from "./useVisibleNotes";
import { auth } from "./firebase";

// Pods tab (view === "pods"), split out of Calendar.js and redesigned 2026-10-10 (mockup: "FriSo Page
// Mockups" artifact). Display only: pods, invitations, contacts, events and every handler that writes
// to Firestore (createPod, inviteMember, removeMemberFromPod, leavePod, renamePod, deletePod) stay in
// Calendar.js and come in as props. Loaded here: a read-only notes list for "Shared notes".
//
// Layout: pod list on the left (your pending invites on top), the selected pod's page on the right.
// On phones the list and the page take turns (tap a pod → page with a back button).

export function PlusIcon() {
  return <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>;
}
export function BackIcon() {
  return <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>;
}
export function PenIcon() {
  return <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16z" /></svg>;
}
export function TrashIcon() {
  return <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>;
}
function MailIcon() {
  return <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M4 7l8 6 8-6" /></svg>;
}
function NoteIcon() {
  return <svg className="pg-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h9l4 4v12H6z" /><path d="M14 4v5h5M9 13h6M9 16h4" /></svg>;
}

// Shared by Pods.js and Contacts.js. nameOf: "You", a contact's name, or the email's first part.
// nameOrEmail: the contact's name, or the full email. initialsOf: avatar letters (yours from your Google name).
export function makeNamer(contacts, currentUserEmail) {
  const byEmail = {};
  Object.values(contacts).forEach((c) => { if (c.email) byEmail[c.email] = c; });
  const contactName = (email) => { const c = byEmail[email]; return (c && (`${c.firstName || ""} ${c.lastName || ""}`.trim() || c.name)) || ""; };
  const nameOf = (email) => (email === currentUserEmail ? "You" : contactName(email) || email?.split("@")[0] || email);
  const nameOrEmail = (email) => contactName(email) || email;
  const initialsOf = (email) => getInitials(email === currentUserEmail ? auth.currentUser?.displayName || email : contactName(email) || email);
  return { nameOf, nameOrEmail, initialsOf };
}

export function PodBubble({ podId, name, size = 44 }) {
  const c = getPodColor(podId);
  return (
    <span className="pg-bub" style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: c.bg, color: "var(--color-text)", boxShadow: `inset 0 0 0 2px ${c.border}` }}>
      {getInitials(name)}
    </span>
  );
}

// On a pod's page its circle is also the color picker. Each person picks their own color (userSettings).
function PodColorButton({ pod, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const outside = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", outside);
    document.addEventListener("touchstart", outside);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("touchstart", outside);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const current = getPodSlot(pod.id);
  return (
    <div className="pg-colorpick" ref={ref}>
      <button type="button" className="pg-colorpick__btn" aria-label="Change this pod's color" aria-expanded={open}
        title="Change color" onClick={() => setOpen((v) => !v)}>
        <PodBubble podId={pod.id} name={pod.name} size={64} />
        <span className="pg-colorpick__badge" aria-hidden="true">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" /></svg>
        </span>
      </button>
      {open && (
        <div className="pg-colorpop" role="group" aria-label="Your color for this pod">
          <div className="pg-colorpop__swatches">
            {POD_COLORS.map((c, i) => (
              <button key={i} type="button" className="pg-swatch" aria-pressed={current === i}
                aria-label={POD_COLOR_NAMES[i]} title={POD_COLOR_NAMES[i]} style={{ background: c.border }}
                onClick={() => { onPick(pod.id, i); setOpen(false); }} />
            ))}
          </div>
          <p className="pg-colorpop__note">Only you see this color.</p>
        </div>
      )}
    </div>
  );
}

const isEmailLike = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

// Pick a contact or type an email. Used by the New pod and Invite dialogs.
export function MemberTypeahead({ contacts, excludeEmails, onAdd, placeholder, autoFocus }) {
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const excluded = new Set((excludeEmails || []).map(normEmail));
  const q = search.trim().toLowerCase();
  const filtered = contacts.filter(
    (c) =>
      !excluded.has(normEmail(c.email)) &&
      (c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
  );
  const canAddFreeform =
    search &&
    isEmailLike(search) &&
    !excluded.has(q) &&
    !filtered.find((c) => normEmail(c.email) === q);

  const commit = (email, name) => {
    onAdd({ id: normEmail(email), email: normEmail(email), name: name || email.trim() });
    setSearch("");
    setShowDropdown(false);
  };

  return (
    <div className="typeahead">
      <input
        className="form-input"
        placeholder={placeholder || "Search contacts or type an email…"}
        value={search}
        autoFocus={autoFocus}
        onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && canAddFreeform) {
            e.preventDefault();
            commit(search.trim(), "");
          }
        }}
      />
      {showDropdown && search && (filtered.length > 0 || canAddFreeform) && (
        <div className="typeahead__dropdown">
          {filtered.map((c) => (
            <div key={c.id} className="typeahead__option" onMouseDown={() => commit(c.email, c.name)}>
              <span className="typeahead__option-name">{c.name === c.email ? c.email : c.name}</span>
              {c.name !== c.email && <span className="typeahead__option-email">{c.email}</span>}
            </div>
          ))}
          {canAddFreeform && (
            <div className="typeahead__option" onMouseDown={() => commit(search.trim(), "")}>
              <span className="typeahead__option-name">Invite "{search.trim()}"</span>
              <span className="typeahead__option-email">They'll see it when they sign in</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePodForm({ onCreate, contacts }) {
  const [name, setName] = useState("");
  const [members, setMembers] = useState([]); // [{id, name, email}]

  const addMember = (contact) => {
    if (members.find((m) => normEmail(m.email) === normEmail(contact.email))) return;
    setMembers((prev) => [...prev, contact]);
  };
  const removeMember = (email) => setMembers((prev) => prev.filter((m) => m.email !== email));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await onCreate(name.trim(), members.map((m) => m.email));
    setName("");
    setMembers([]);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label" htmlFor="pod-name">Pod name</label>
        <input id="pod-name" className="form-input" placeholder="e.g. Family, Climbing crew" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
      <div className="form-group">
        <label className="form-label">Invite people</label>
        {members.length > 0 && (
          <div className="chips">
            {members.map((m) => (
              <span key={m.email} className="chip">
                {m.name !== m.email && m.name ? m.name : m.email}
                <button type="button" className="chip__remove" onClick={() => removeMember(m.email)}>×</button>
              </span>
            ))}
          </div>
        )}
        <MemberTypeahead contacts={contacts} excludeEmails={members.map((m) => m.email)} onAdd={addMember}
          placeholder="Search contacts or type an email…" />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="submit" className="btn btn--primary pg-btn" disabled={!name.trim()}>Create pod</button>
      </div>
    </form>
  );
}

// A small dialog (portaled overlay, like every modal) with a title and ✕.
export function Dialog({ title, onClose, children }) {
  return createPortal(
    <div className="modal-overlay" onClick={onClose} onTouchMove={(e) => e.stopPropagation()}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div className="modal__title">{title}</div>
          <button type="button" className="btn btn--ghost btn--sm" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="modal__content">{children}</div>
      </div>
    </div>,
    document.querySelector(".app-shell") || document.body
  );
}

export default function Pods({
  pods, invitations, pendingInvitations = [], onAcceptInvitation, onDeclineInvitation,
  contacts, typeaheadContacts, currentUserUid, currentUserEmail, isPodOwner,
  allEventsForDisplay, isEventVisibleToUser,
  onCreatePod, onInviteMember, onRemoveMember, onLeavePod, onRenamePod, onDeletePod, onPickPodColor,
  onPlanWithPod, onOpenEvent, onOpenNote, podIntent = null, onIntentDone = () => {},
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false); // phones: the pod page replaces the list
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [renaming, setRenaming] = useState(null); // draft name while renaming
  const notes = useVisibleNotes(currentUserUid, currentUserEmail);
  const { nameOf, nameOrEmail, initialsOf } = makeNamer(contacts, currentUserEmail);

  const now = new Date();
  const todayKey = localDateKey(now);
  const nowHM = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const visible = allEventsForDisplay.filter(isEventVisibleToUser);

  // Upcoming shared plans per pod (busy/away blocks aren't plans), soonest first
  const info = Object.values(pods).map((pod) => {
    const upcoming = visible
      .filter((ev) => (ev.sharedPods || []).includes(pod.id) && getEventKind(ev) === "event")
      .filter((ev) => ev.startDate > todayKey || (ev.startDate === todayKey && (!ev.endTime || ev.endTime > nowHM)))
      .sort((a, b) => (a.startDate + (a.startTime || "")).localeCompare(b.startDate + (b.startTime || "")));
    return { pod, upcoming };
  }).sort((a, b) => {
    const x = a.upcoming[0], y = b.upcoming[0];
    if (!x !== !y) return x ? -1 : 1;
    if (x && y) {
      const c = (x.startDate + (x.startTime || "")).localeCompare(y.startDate + (y.startTime || ""));
      if (c) return c;
    }
    return a.pod.name.localeCompare(b.pod.name);
  });

  const nextLabel = (ev) => ev ? `${ev.title} · ${relDay(ev.startDate, todayKey)}${ev.startTime ? ` ${formatTime(ev.startTime)}` : ""}` : "Nothing planned";
  const current = info.find((p) => p.pod.id === selectedId) || info[0] || null;
  const open = (id) => { setSelectedId(id); setDetailOpen(true); setRenaming(null); document.querySelector(".app-main")?.scrollTo(0, 0); };
  // Opened from a notification ("X left your pod"): open that pod's page, if it still exists
  useEffect(() => {
    if (!podIntent) return;
    if (pods[podIntent.podId]) open(podIntent.podId);
    onIntentDone();
  }, [podIntent]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderDetail = ({ pod, upcoming }) => {
    const owner = isPodOwner(pod);
    const members = pod.members || [];
    const pending = invitations.filter((inv) => inv.podId === pod.id && inv.status === "pending");
    const podNotes = notes
      .filter((n) => (n.sharedPods || []).includes(pod.id) && !(n.hiddenBy || []).includes(currentUserEmail))
      .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0))
      .slice(0, 6);
    const ownerName = pod.ownerEmail ? nameOf(pod.ownerEmail) : null;
    const saveName = async () => {
      if (renaming && renaming.trim() && renaming.trim() !== pod.name) await onRenamePod(pod.id, renaming);
      setRenaming(null);
    };
    // Owner first, then members A–Z; "You" stays in place
    const sorted = [...members].sort((a, b) => (a === pod.ownerEmail ? -1 : b === pod.ownerEmail ? 1 : nameOf(a).localeCompare(nameOf(b))));

    return (
      <section className="card pg-detail" aria-label={pod.name}>
        <button type="button" className="pg-back" onClick={() => setDetailOpen(false)}><BackIcon />Pods</button>
        <div className="pg-dhead">
          <PodColorButton key={pod.id} pod={pod} onPick={onPickPodColor} />
          <div className="pg-dhead__text">
            {renaming !== null ? (
              <input className="form-input pg-rename" value={renaming} autoFocus maxLength={60} aria-label="Pod name"
                onChange={(e) => setRenaming(e.target.value)} onBlur={saveName}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRenaming(null); }} />
            ) : (
              <h2 className="pg-title">
                {pod.name}
                {owner && <button type="button" className="pg-iconbtn" aria-label="Rename pod" title="Rename pod" onClick={() => setRenaming(pod.name)}><PenIcon /></button>}
              </h2>
            )}
            <div className="pg-sub">
              {members.length} {members.length === 1 ? "member" : "members"}
              {owner ? " · You own this pod" : ownerName ? ` · Owned by ${ownerName}` : ""}
            </div>
          </div>
          <div className="pg-dhead__actions">
            {owner && <button type="button" className="btn btn--secondary pg-btn" onClick={() => setShowInvite(true)}><MailIcon />Invite</button>}
            <button type="button" className="btn btn--primary pg-btn" onClick={() => onPlanWithPod(pod.id)}><PlusIcon />Plan something</button>
          </div>
        </div>

        <div className="pg-cols">
          <div className="pg-sec">
            <div className="dash-eyebrow">Members</div>
            {sorted.map((email) => (
              <div key={email} className="pg-member">
                <span className="pg-face">{initialsOf(email)}</span>
                <span className="pg-member__name">{nameOf(email)}<small>{email}</small></span>
                {email === pod.ownerEmail ? <span className="pg-role">Owner</span>
                  : owner ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => onRemoveMember(pod.id, email)}>Remove</button>
                  : null}
              </div>
            ))}
            {pending.map((inv) => (
              <div key={inv.id} className="pg-member">
                <span className="pg-face pg-face--pending"><MailIcon /></span>
                <span className="pg-member__name">{nameOrEmail(inv.inviteeEmail)}<small>Invited · hasn't joined yet</small></span>
                <span className="pg-role pg-role--pending">Pending</span>
              </div>
            ))}
          </div>

          <div className="pg-stack">
            <div className="pg-sec">
              <div className="dash-eyebrow">Coming up together</div>
              {upcoming.length === 0 ? (
                <p className="dash-muted">Nothing planned yet.</p>
              ) : upcoming.slice(0, 4).map((ev) => (
                <button key={ev.id} type="button" className="pg-plan" onClick={(e) => onOpenEvent(ev, e.currentTarget)}>
                  <span className="pg-date"><small>{ev.startDate === todayKey ? "Today" : shortDay(ev.startDate)}</small><b>{Number(ev.startDate.slice(8))}</b></span>
                  <span className="pg-plan__text">{ev.title}<small>{ev.startTime ? formatTime(ev.startTime) : "All day"}{ev.location ? ` · ${ev.location}` : ""}</small></span>
                </button>
              ))}
            </div>
            <div className="pg-sec">
              <div className="dash-eyebrow">Shared notes</div>
              {podNotes.length === 0 ? (
                <p className="dash-muted">No notes shared with this pod.</p>
              ) : (
                <div className="pg-chips">
                  {podNotes.map((n) => (
                    <button key={n.id} type="button" className="pg-chip" onClick={() => onOpenNote(n.id)}><NoteIcon />{n.title || "Untitled note"}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pg-foot">
          {owner ? (
            <button type="button" className="btn btn--ghost btn--sm pg-danger" onClick={() => onDeletePod(pod.id)}><TrashIcon />Delete pod</button>
          ) : (
            <button type="button" className="btn btn--ghost btn--sm pg-danger" onClick={() => onLeavePod(pod.id)}>Leave pod</button>
          )}
        </div>
      </section>
    );
  };

  const invitePod = current && showInvite ? current.pod : null;
  const inviteExclude = invitePod ? [
    ...(invitePod.members || []),
    ...invitations.filter((inv) => inv.podId === invitePod.id && inv.status === "pending").map((inv) => inv.inviteeEmail),
  ] : [];

  return (
    <div className="pg">
      <div className="pg-head">
        <h1 className="section-title pg-h1">Pods</h1>
        <button type="button" className="btn btn--primary pg-btn" onClick={() => setShowCreate(true)}><PlusIcon />New pod</button>
      </div>

      <div className={`pg-two${detailOpen ? " pg-two--open" : ""}`}>
        <section className="card pg-list" aria-label="Your pods">
          {pendingInvitations.map((inv) => {
            const c = getPodColor(inv.podId);
            return (
              <div key={inv.id} className="dash-invite" style={{ background: c.bg, color: c.text }}>
                <div><b>{nameOf(inv.inviterEmail)}</b> invited you to <b>{inv.podName}</b></div>
                <div className="dash-invite__actions">
                  <button type="button" className="btn btn--primary btn--sm" disabled={inv._joining} onClick={() => onAcceptInvitation(inv)}>{inv._joining ? "Joining…" : "Join"}</button>
                  <button type="button" className="btn btn--secondary btn--sm" disabled={inv._joining} onClick={() => onDeclineInvitation(inv)}>Not now</button>
                </div>
              </div>
            );
          })}
          {info.length === 0 && (
            <div className="pg-empty">
              <p className="dash-muted">Pods are small groups you share plans and notes with. Start one for your family, roommates or a club.</p>
              <button type="button" className="btn btn--secondary pg-btn" onClick={() => setShowCreate(true)}><PlusIcon />New pod</button>
            </div>
          )}
          {info.map(({ pod, upcoming }) => (
            <button key={pod.id} type="button" className="pg-row pg-podrow" aria-current={current?.pod.id === pod.id}
              onClick={() => open(pod.id)}>
              <PodBubble podId={pod.id} name={pod.name} />
              <span className="pg-row__text">
                <b>{pod.name}</b>
                <small>{nextLabel(upcoming[0])}</small>
                <span className="dash-faces">
                  {(pod.members || []).slice(0, 5).map((m) => <span key={m} className="dash-face">{initialsOf(m)}</span>)}
                  {(pod.members || []).length > 5 && <span className="dash-face dash-face--more">+{pod.members.length - 5}</span>}
                </span>
              </span>
            </button>
          ))}
        </section>

        {current && renderDetail(current)}
      </div>

      {showCreate && (
        <Dialog title="New pod" onClose={() => setShowCreate(false)}>
          <CreatePodForm contacts={typeaheadContacts}
            onCreate={async (name, members) => { await onCreatePod(name, members); setShowCreate(false); }} />
        </Dialog>
      )}

      {invitePod && (
        <Dialog title={`Invite to ${invitePod.name}`} onClose={() => setShowInvite(false)}>
          <p className="dash-muted" style={{ marginBottom: 12 }}>They'll see the invite in FriSo when they sign in with this email.</p>
          <MemberTypeahead
            autoFocus
            contacts={typeaheadContacts.filter((c) => !inviteExclude.includes(c.email))}
            excludeEmails={inviteExclude}
            onAdd={async (contact) => { await onInviteMember(invitePod.id, contact.email); }}
            placeholder="Search contacts or type an email…"
          />
        </Dialog>
      )}
    </div>
  );
}
